import { useState, useCallback } from 'react';
import { useAccount, useSignMessage } from 'wagmi';
import { walletApi } from '../api/wallet';

export type LinkOutcome =
  | { ok: true }
  | { ok: false; error: string };

interface LinkResult {
  linking: boolean;
  /** Last error message — kept in state for callers that want to render it. */
  error: string | null;
  /** Run the full challenge → sign → link flow. Returns a discriminated result. */
  link: () => Promise<LinkOutcome>;
}

/**
 * SIWE-style wallet linking:
 *   1. Request a challenge from backend (server returns a message containing a nonce)
 *   2. User signs the message with their connected wallet
 *   3. POST message + signature to backend to link wallet to current authenticated user
 *
 * Returns a {ok, error} object so callers can show the real failure reason
 * in a toast instead of a generic "Wallet linking failed" message.
 */
export function useLinkWallet(): LinkResult {
  const { address } = useAccount();
  const { signMessageAsync } = useSignMessage();
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const link = useCallback(async (): Promise<LinkOutcome> => {
    if (!address) {
      const msg = 'Connect a wallet first';
      setError(msg);
      return { ok: false, error: msg };
    }
    setLinking(true);
    setError(null);
    let step: 'challenge' | 'sign' | 'link' = 'challenge';
    try {
      const challenge = await walletApi.challenge(address);

      step = 'sign';
      const signature = await signMessageAsync({ message: challenge.message });

      step = 'link';
      await walletApi.link({ message: challenge.message, signature });

      return { ok: true };
    } catch (err: unknown) {
      const e = err as { shortMessage?: string; message?: string; name?: string };
      const raw = e.shortMessage ?? e.message ?? 'Linking failed';

      // Friendly mapping for common wallet-side errors so the toast is readable
      let friendly = raw;
      if (/user rejected|user denied|rejected the request/i.test(raw)) {
        friendly = 'You rejected the signature in your wallet.';
      } else if (/timeout|timed out|expired/i.test(raw)) {
        friendly = 'Signature request timed out. Re-open MetaMask and try again.';
      } else if (/disconnect|not connected/i.test(raw)) {
        friendly = 'Wallet disconnected before signing. Reconnect and try again.';
      }

      // Surface in browser console so dev tools shows full error
      // eslint-disable-next-line no-console
      console.error(`[useLinkWallet] failed at step "${step}":`, raw, err);

      const msgWithStep = step === 'link'
        ? friendly
        : `${friendly} (failed at: ${step})`;
      setError(msgWithStep);
      return { ok: false, error: msgWithStep };
    } finally {
      setLinking(false);
    }
  }, [address, signMessageAsync]);

  return { linking, error, link };
}
